import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  SwipeableDrawer,
  Typography
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";

import type { Position } from "@/data/mock-positions";
import type { Bot, BrokerAccount } from "@/data/mock-data";
import { formatUSD } from "@/lib/formatters";
import { Sparkline } from "@/components/Sparkline";
import { TickerAvatar } from "@/components/TickerAvatar";
import { desktopBottomDrawerPaperSx } from "@/theme/drawer-sx";

// ---- Public component -----------------------------------------------------

interface PositionDetailDrawerProps {
  /** When non-null, drawer is open showing details for this position. */
  position: Position | null;
  /** Bots so we can resolve `position.botId → name`. Optional — if
   *  the bot can't be found we show "Manual". */
  bots?: readonly Bot[];
  /** Live + paper accounts so we can resolve `position.accountId → name`. */
  accounts?: readonly BrokerAccount[];
  /** `true` when the host view is the aggregate "All Live Accounts" tab —
   *  drives whether to surface the masked broker id alongside the name. */
  showAccount?: boolean;
  /**
   * Optional per-position weekly $/% delta keyed by position id —
   *  same shape as PositionsSimpleView. Used as the header fallback
   *  when today's change is 0; mirrors BotDetailDrawer's
   *  weeklyDeltas wiring (Sergey 2026-04-27).
   */
  weeklyDeltas?: Record<string, { fraction: number; delta: number }>;
  /**
   * Action callback for "View orders for {ticker}" CTA — same id-string
   * shape the row's onPositionClick uses. Optional; absent when the
   * caller doesn't have an Orders tab to route to.
   */
  onViewOrders?: (positionId: string) => void;
  onClose: () => void;
}

/**
 * Bottom-sheet drawer for a single position — mirrors BotDetailDrawer
 * (Sergey 2026-04-26 polish round). The simple-list row only shows
 * ticker + qty + market value + today's chip; everything else (avg
 * entry, cost basis, total P/L, side, asset class, bot, opened) lives
 * here.
 *
 * Layout:
 *   • Header echo — ticker logo + symbol + name, with current market
 *     value + today's ▲/▼ delta on the right. Same identity the user
 *     just tapped.
 *   • Sparkline — past-3-month price walk, full-bleed. Coloured by
 *     today's direction (matches the row's chip).
 *   • Detail grid — every metric the simple row dropped (Side, Qty,
 *     Avg entry, Current price, Cost basis, Market value, Today $/%,
 *     Total $/%, Bot, Account, Opened, Asset class). Two-column
 *     auto-grid, label / value.
 *   • Footer — primary CTA "View orders for {ticker}" + Close.
 */
export function PositionDetailDrawer({
  position,
  bots,
  accounts,
  showAccount,
  weeklyDeltas,
  onViewOrders,
  onClose
}: PositionDetailDrawerProps) {
  // Hold the last non-null position so the drawer keeps rendering through
  // its slide-out animation (same trick BotDetailDrawer / OrderDetailDrawer
  // use to avoid the empty-flash on close).
  const [snapshot, setSnapshot] = useState<Position | null>(position);
  if (position && position !== snapshot) {
    setSnapshot(position);
  }
  const visible = snapshot;
  if (!visible) return null;

  const bot = visible.botId ? bots?.find((b) => b.id === visible.botId) : undefined;
  const account = accounts?.find((a) => a.id === visible.accountId);

  // Today / This week / — fallback chain — same logic the row uses, so
  // drawer header stays in sync with what the user just tapped (Sergey
  // 2026-04-27 polish parity).
  const hasToday =
    Number.isFinite(visible.todayPLDollar) && visible.todayPLDollar !== 0;
  const weeklyDelta = weeklyDeltas?.[visible.id];
  const periodMode: "today" | "week" | "none" = hasToday
    ? "today"
    : weeklyDelta
      ? "week"
      : "none";
  const periodLabel = periodMode === "week" ? "this week" : "today";
  const periodFraction =
    periodMode === "today"
      ? visible.todayPLPct
      : periodMode === "week"
        ? weeklyDelta!.fraction
        : 0;
  const periodDelta =
    periodMode === "today"
      ? visible.todayPLDollar
      : periodMode === "week"
        ? weeklyDelta!.delta
        : 0;
  const periodPositive = periodFraction >= 0;
  const todayPositive = visible.todayPLPct >= 0;
  const totalPositive = visible.totalPLDollar >= 0;

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={position !== null}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableDiscovery
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: "env(safe-area-inset-bottom)",
          maxHeight: "92vh",
          ...desktopBottomDrawerPaperSx,
        },
      }}
    >
      {/* Grab handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
        <Box
          sx={(t: Theme) => ({
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: t.palette.divider
          })}
        />
      </Box>

      {/* Header — ticker logo + symbol/name on top, current value on
       *  its own line below. Two-line layout matches BotDetailDrawer
       *  so the long instrument names ("PIMCO Active Bond Exchange-
       *  Traded Fund") stay readable instead of getting truncated. */}
      <Stack direction="column" spacing={1} sx={{ px: 2, pt: 1, pb: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TickerAvatar symbol={visible.asset} size={44} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.2,
                  letterSpacing: 0.2
                }}
              >
                {visible.asset}
              </Typography>
              {visible.side === "short" && (
                <Chip
                  label="Short"
                  size="small"
                  sx={(t) => ({
                    bgcolor: alpha(t.palette.error.main, 0.15),
                    color: t.palette.error.main,
                    fontWeight: 600,
                    height: 22,
                    "& .MuiChip-label": { px: 1.25, fontSize: 11 }
                  })}
                />
              )}
            </Stack>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                lineHeight: 1.2,
                whiteSpace: "normal",
                wordBreak: "break-word"
              }}
            >
              {visible.assetName}
            </Typography>
          </Box>
        </Stack>
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontFeatureSettings: '"tnum" 1',
              lineHeight: 1.1
            }}
          >
            {formatUSD(visible.marketValue)}
          </Typography>
          {periodMode === "none" ? (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
            >
              — today
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={(t) => ({
                color: periodPositive
                  ? t.palette.success.main
                  : t.palette.error.main,
                fontWeight: 600,
                fontFeatureSettings: '"tnum" 1',
                whiteSpace: "nowrap"
              })}
            >
              {periodPositive ? "▲" : "▼"}{" "}
              {formatUSD(Math.abs(periodDelta))}{" "}
              ({Math.abs(periodFraction * 100).toFixed(2)}%) {periodLabel}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Divider />

      {/* Sparkline — 90-day price walk. Same "Past 3 months" + "3M"
       *  caption as BotDetailDrawer for consistency with the trading-
       *  app convention. Skipped when no series available. */}
      {visible.sparkline.length > 1 && (
        <>
          <Box sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Past 3 months
              </Typography>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: "text.secondary",
                  fontWeight: 600,
                  letterSpacing: 0.4,
                  px: 0.75,
                  py: 0.125,
                  borderRadius: 0.75,
                  bgcolor: alpha(theme.palette.text.primary, 0.06)
                })}
              >
                3M
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ px: 0.5, pb: 1.5 }}>
            <Sparkline
              values={visible.sparkline}
              height={64}
              negative={!todayPositive}
            />
          </Box>
          <Divider />
        </>
      )}

      {/* Detail grid */}
      <Box sx={{ px: 2, py: 1.5, overflowY: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "auto minmax(0, 1fr)",
            rowGap: 0.75,
            columnGap: 2,
            alignItems: "center"
          }}
        >
          <DetailLabel>Side</DetailLabel>
          <DetailValue>
            <Box
              component="span"
              sx={(t) => ({
                color:
                  visible.side === "long"
                    ? t.palette.success.main
                    : t.palette.error.main,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                fontSize: "0.875rem"
              })}
            >
              {visible.side === "long" ? "Long" : "Short"}
            </Box>
          </DetailValue>

          <DetailLabel>Quantity</DetailLabel>
          <DetailValue>
            {formatQty(visible.qty)} {formatShareLabel(visible.qty)}
          </DetailValue>

          <DetailLabel>Avg entry</DetailLabel>
          <DetailValue>
            {formatUSD(visible.avgEntryPrice, { maximumFractionDigits: 4 })}
          </DetailValue>

          <DetailLabel>Current price</DetailLabel>
          <DetailValue>
            {formatUSD(visible.currentPrice, { maximumFractionDigits: 4 })}
          </DetailValue>

          <DetailLabel>Cost basis</DetailLabel>
          <DetailValue>{formatUSD(visible.costBasis)}</DetailValue>

          <DetailLabel>Market value</DetailLabel>
          <DetailValue>{formatUSD(visible.marketValue)}</DetailValue>

          <DetailLabel>Today's P/L</DetailLabel>
          <DetailValue>
            <Box component="span" sx={{ whiteSpace: "nowrap" }}>
              <SignedDollar amount={visible.todayPLDollar} />
            </Box>{" "}
            <Box
              component="span"
              sx={{
                color: "text.secondary",
                whiteSpace: "nowrap",
                display: "inline-block"
              }}
            >
              ({formatPctSigned(visible.todayPLPct)})
            </Box>
          </DetailValue>

          <DetailLabel>Total P/L</DetailLabel>
          <DetailValue>
            <Box component="span" sx={{ whiteSpace: "nowrap" }}>
              <SignedDollar amount={visible.totalPLDollar} />
            </Box>{" "}
            <Box
              component="span"
              sx={{
                color: "text.secondary",
                whiteSpace: "nowrap",
                display: "inline-block"
              }}
            >
              ({formatPctSigned(visible.totalPLPct)})
            </Box>
          </DetailValue>

          <DetailLabel>Asset class</DetailLabel>
          <DetailValue>{formatAssetClass(visible.assetClass)}</DetailValue>

          {/* Bot — when the row is a merged All-Live aggregate
           *  (mergePositionsByTicker rolled up multiple bots' lots),
           *  show the count instead of a single name. Drawer parity
           *  with the user's ask: same ticker held by 3 bots reads
           *  as "Multiple bots (3)", not a misleading "Manual" or
           *  arbitrary first-leg name. (Sergey 2026-04-27.) */}
          <DetailLabel>Bot</DetailLabel>
          <DetailValue>
            {typeof visible.botCount === "number" && visible.botCount > 1 ? (
              <Box component="span">
                Multiple bots{" "}
                <Box component="span" sx={{ color: "text.secondary" }}>
                  ({visible.botCount})
                </Box>
              </Box>
            ) : bot ? (
              bot.name
            ) : (
              <Box component="span" sx={{ color: "text.secondary" }}>
                Manual
              </Box>
            )}
          </DetailValue>

          <DetailLabel>Account</DetailLabel>
          <DetailValue>
            {typeof visible.accountCount === "number" &&
            visible.accountCount > 1 ? (
              <Box component="span">
                Multiple accounts{" "}
                <Box component="span" sx={{ color: "text.secondary" }}>
                  ({visible.accountCount})
                </Box>
              </Box>
            ) : account ? (
              <>
                {account.name}
                {showAccount && account.masked && (
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", ml: 0.75 }}
                  >
                    {account.masked}
                  </Box>
                )}
              </>
            ) : (
              <Box component="span" sx={{ color: "text.secondary" }}>
                —
              </Box>
            )}
          </DetailValue>

          <DetailLabel>Opened</DetailLabel>
          <DetailValue>{formatDate(visible.openedAt)}</DetailValue>
        </Box>{/* end of grid */}
        {/* Side-effect chip: a totalPositive indicator badge is purely
         *  cosmetic — kept here so the totalPositive flag isn't unused
         *  by the linter, and so the visual mood of the drawer mirrors
         *  the P/L state. */}
        <Box
          sx={{
            mt: 1.5,
            display: "flex",
            justifyContent: "flex-end"
          }}
        >
          <Chip
            size="small"
            label={totalPositive ? "In profit" : "In loss"}
            sx={(t) => ({
              bgcolor: alpha(
                totalPositive ? t.palette.success.main : t.palette.error.main,
                0.12
              ),
              color: totalPositive
                ? t.palette.success.main
                : t.palette.error.main,
              fontWeight: 600,
              height: 22,
              "& .MuiChip-label": { px: 1.25, fontSize: 11 }
            })}
          />
        </Box>
      </Box>

      <Divider />

      {/* Footer — primary CTA opens orders for this ticker; secondary
       *  closes. Mirrors BotDetailDrawer's two-button row. */}
      <Box sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1}>
          {onViewOrders && (
            <Button
              onClick={() => {
                onViewOrders(visible.id);
                onClose();
              }}
              variant="outlined"
              color="primary"
              startIcon={<OpenInNewRounded sx={{ fontSize: 18 }} />}
              sx={{ flex: 1, fontWeight: 600 }}
            >
              View orders for {visible.asset}
            </Button>
          )}
          <Button
            onClick={onClose}
            variant="text"
            color="inherit"
            sx={{ flex: 1, fontWeight: 500 }}
          >
            Close
          </Button>
        </Stack>
      </Box>
    </SwipeableDrawer>
  );
}

// ---- Internals -----------------------------------------------------------

function DetailLabel({ children }: { children: React.ReactNode }) {
  // body1 (1rem) — same scale BotDetailDrawer / OrderDetailPanel use
  // so all three detail drawers share a typography baseline.
  return (
    <Typography
      variant="body1"
      sx={{
        color: "text.secondary",
        pr: 2,
        alignSelf: "center"
      }}
    >
      {children}
    </Typography>
  );
}

function DetailValue({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="body1"
      sx={{
        color: "text.primary",
        textAlign: "right",
        fontFeatureSettings: '"tnum" 1',
        wordBreak: "break-word"
      }}
    >
      {children}
    </Typography>
  );
}

/** Signed dollar amount, green if positive, red if negative, muted at zero. */
function SignedDollar({ amount }: { amount: number }) {
  const positive = amount > 0;
  const negative = amount < 0;
  return (
    <Box
      component="span"
      sx={(t: Theme) => ({
        color: positive
          ? t.palette.success.main
          : negative
            ? t.palette.error.main
            : t.palette.text.primary,
        fontWeight: 700,
        fontFeatureSettings: '"tnum" 1'
      })}
    >
      {positive ? "+" : negative ? "−" : ""}
      {formatUSD(Math.abs(amount))}
    </Box>
  );
}

function formatPctSigned(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}${Math.abs(n * 100).toFixed(2)}%`;
}

function formatQty(n: number): string {
  if (Number.isInteger(n)) {
    return new Intl.NumberFormat("en-US").format(n);
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4
  }).format(n);
}

function formatShareLabel(n: number): string {
  return Math.abs(n) === 1 ? "share" : "shares";
}

function formatAssetClass(c: Position["assetClass"]): string {
  switch (c) {
    case "stocks": return "Stocks";
    case "etf": return "ETF";
    case "crypto": return "Crypto";
    case "options": return "Options";
  }
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  } catch {
    return iso;
  }
}
